import axios, { AxiosError } from 'axios';
import { ActivitySearchParams } from '../models/Activity';
import AppFile from '../models/AppFile';
import EquivalencyRequest, { EquivalencyRequestActivityStates, EquivalencyRequestParams, EquivalencyRequestSearchParams, EquivalencyRequestSimulationSearchParams, RequestActivity, PreviousEquivalencyRequestParams } from '../models/EquivalencyRequest';
import { EquivalencyRuleSearchParams } from '../models/EquivalencyRule';
import Institution, { InstitutionSearchParams } from '../models/Institution';
import DealData from '../models/DealData';
import Student, { studentSearchParams } from '../models/Student';
import Syllabus, { CompatibleSyllabusSearchParams, SyllabusSearchParams } from '../models/Syllabus';
import AppEnviroments from '../application//AppEnviroments';
import { getBaseUrl, getPostFormHubspotUrl } from '../shared/utils/commons';
import { FormDataHubspotForms } from '../shared/utils/formatHubspotForms';
import { retryPromises } from '../shared/utils/RetryPromises';
import { delay } from '../shared/utils/delay';
import { t } from 'i18next';
import User from '../models/User';
import { HubspotContactParams } from '../models/HubspotContact';
import { ReviewRequesttParams } from '../models/ReviewRequest';
import { StudentRecordsSyllabusesParams } from '../models/StudentRecord';

export interface PaginatedResponse<T> {
  items: T[]
  count: number
  offset: number
}

export type ErrorResponse<T> = AxiosError<Error, T>

const baseURL = !process.env.NODE_ENV || process.env.NODE_ENV === 'development' ? process.env.REACT_APP_API_URL : `${getBaseUrl()}/v1`;
const loginURL = baseURL + '/login';

const controller = new AbortController();

export const abortConnections = () => {
  controller.abort();
};

const axiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    Accept: 'application/json'
  }
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (err) => {
    const { response } = err;
    const isLogout = globalThis.location.pathname.includes('logout');
    if (!isLogout && response && response.status === 401) {
      API.goToLogin();
    } else if (response && response.status >= 500) {
      console.error(response);
    }
    return Promise.reject(new Error(err?.message ?? 'Unknown error'));
  }
);

export class API {
  static async initApp() {
    let user = null;

    try {
      user = await API.getUser();
    } catch (e) {
      const error = e as ErrorResponse<User>
      if (error.response && error.response.status === 403) {
        user = {
          authorities: []
        };
      }
    }

    return { user };
  }

  static goToLogin() {
    globalThis.location.replace(loginURL);
  }

  static async getUser() {
    const { data } = await axiosInstance.get('/users');
    return data;
  }

  static async getSettings() {
    const { data } = await axiosInstance.get('/settings');
    return data;
  }

  static async getStudent(parameters: studentSearchParams) {
    const { data } = await axiosInstance.get('/student', {
      params: parameters
    });
    return data;
  }

  static async getInstitutions(parameters: InstitutionSearchParams, signal?: AbortSignal) {
  let options = { params: parameters};
  if(signal!==undefined && signal!==null){
  options = { params: parameters, signal: signal } as { params: InstitutionSearchParams; signal?: AbortSignal };
  }
    const { data } = await axiosInstance.get('/institution', options);
    return data;
  }

  static async getInstitution(institutionId: Institution['id']) {
    const { data } = await axiosInstance.get(`/institution/${institutionId}`);
    return data;
  }

  static async getTargetInstitution() {
    const { data } = await axiosInstance.get<PaginatedResponse<Institution>>('/institution', {
      params: {
        pattern: AppEnviroments.TARGET_INSTITUTION
      }
    });
    const institution = data.items.find(a => a.short_name === AppEnviroments.TARGET_INSTITUTION);
    return institution;
  }

  static async putInstitution(institution: Institution) {
    const id = institution.id;
    const parameter = {
      ...institution,
      id: undefined
    };
    const { data } = await axiosInstance.put(`/institution/${id}`, parameter);
    return data;
  }

  static async putSyllabus(institutionId: Institution['id'], syllabus: Syllabus) {
    const syllabusId = syllabus.id;
    const syllabusSearch = {
      ...syllabus,
      id: undefined
    }
    const { data } = await axiosInstance.put(`/institution/${institutionId}/syllabus/${syllabusId}`, syllabusSearch);
    return data;
  }

  static async getSyllabusInInstitution(parameters: SyllabusSearchParams) {
    const { data } = await axiosInstance.get(`/institution/${parameters.institutionId}/syllabus`, {
      params: parameters
    });
    return data;
  }

  static async getCardinality(parameters: { ruleIds: string[] }) {
    const { data } = await axiosInstance.post('/equivalency/rule/cardinality', parameters.ruleIds);
    return data;
  }

  static async getSyllabusCompatibilityInInstitution(parameters: CompatibleSyllabusSearchParams) {
    const { data } = await axiosInstance.get(`/institution/${parameters.institutionId}/syllabus/${parameters.sourceSyllabusId}`, {
      params: { compatibilityCheck: parameters.compatibilityCheck }
    });
    return data;
  }

  static async getSyllabusActivities(parameters: ActivitySearchParams) {
    const { hideElectives = false } = parameters;
    const { data } = await axiosInstance.get(`/institution/${parameters.institutionId}/syllabus/${parameters.syllabusId}`, {
      params: {
        hideElectives: hideElectives
      }
    });
    return data;
  }

  static async getStudentRecordsSyllabuses(parameters: StudentRecordsSyllabusesParams) {
    const { document_type, document_number } = parameters;
    const { data } = await axiosInstance.get(`/student/student/studentRecord?document_type=${document_type}&document_number=${document_number}`)
    return data;
  }

  static async getSimulateEquivalencyRequest(reqBody: EquivalencyRequestSimulationSearchParams) {
    const { data } = await axiosInstance.post('/equivalency/request/simulate', reqBody);
    return data;
  }

  static async getPreviousEquivalencyRequest(input: PreviousEquivalencyRequestParams) {
    const body = {
      documentType: input.documentType,
      documentNumber: input.documentNumber,
      state: input.state,
      sourceSyllabusName: input.sourceSyllabusName,
      sourceSyllabusId: input.sourceSyllabusId,
      sourceActivities: input.sourceActivities,
      targetSyllabusId: input.targetSyllabusId
    }
    const newToken = input.generateToken && await input.generateToken();
    const params = {
      pageSize: input.pageSize,
      offset: input.offset,
      reCaptchaToken: newToken
    }
    const { data } = await axiosInstance.post('/equivalency/request', body, { params });
    return data;
  }

  static async getRequestBySearch(input: EquivalencyRequestSearchParams) {
    const body = {
      documentType: input.documentType,
      documentNumber: input.documentNumber,
      caseNumber: input.caseNumber
    }
    const params = {
      pageSize: input.pageSize,
      offset: input.offset,
      reCaptchaToken: input.reCaptchaToken
    }
    const { data } = await axiosInstance.post<PaginatedResponse<EquivalencyRequest>>(
      `/equivalency/request/`, body, { params }
    );

    const existSpecificRequest = data.items.find((request) => request.case_number === input.caseNumber)

    if (existSpecificRequest) {
      const equivalency = await axiosInstance.get(`/equivalency/request/${existSpecificRequest.id}`)
      return {
        ...existSpecificRequest,
        equivalencies: equivalency.data
      }
    }
    if (input.id) {
      const equivalency = await axiosInstance.get(`/equivalency/request/${input.id}`)
      return {
        equivalencies: equivalency.data
      }
    }

    return null
  }

  static async putStudent(student: Student) {
    const suuidId = student.id;
    const searchStudent = {
      ...student,
      id: undefined
    }
    const { data } = await axiosInstance.put(`/student/${suuidId}`, searchStudent);
    return data;
  }

  static async putRequest(params: EquivalencyRequestParams, isEditFile?: boolean) {
    const request = params.request;
    const ruuid = request.id;
    const searchRequest = {
      ...request,
      id: undefined
    }
    let newSearchRequest = { ...searchRequest }
    if (!isEditFile) {
      const modifiedStudentFiles = API.renameDocumentsName(searchRequest.files);
      const modifiedactivitiesFiles = API.renameActivitiesFileName(searchRequest.activities);
      newSearchRequest = { ...searchRequest, files: modifiedStudentFiles, activities: modifiedactivitiesFiles };
    }

    const fileUploadPromises = [
      ...newSearchRequest.activities.map(async activity => {
        if (activity.program_file !== undefined && activity.program_file !== null && (activity.program_file.url === undefined || activity.program_file.url === '')) {
          const updatedFile = await API.getFileURL(ruuid, activity.program_file);
          activity.program_file = {
            ...activity.program_file,
            url: updatedFile.url
          };
          return API.uploadActivityFiles(activity);
        }
      }
      ),
      ...newSearchRequest.files.map(async file => {
        if (file !== undefined && file !== null && (file.url === undefined || file.url === '')) {
          const updatedFile = await API.getFileURL(ruuid, file);
          file = {
            ...file,
            url: updatedFile.url
          };
          return API.uploadDocumentFiles(file);
        }
      }
      )
    ]

    const delayedUploads = fileUploadPromises.map((promise, index) => {
      const millis = index * 100
      return delay(promise, millis)
    })

    await retryPromises(delayedUploads, 3);
    const newToken = params.generateToken && await params.generateToken();
    newSearchRequest = {
      ...newSearchRequest,
      reCaptchaToken: newToken
    }
    const { data } = await axiosInstance.put<EquivalencyRequest>(`/equivalency/request/${ruuid}`, newSearchRequest);
    return data;
  }

  static async getFileURL(requestId: string, file: AppFile) {
    const { data } = await axiosInstance.post<AppFile>(`/equivalency/request/${requestId}/file`, file);
    return data;
  }

 
   static async getRequestById(requestId: string) {
    const { data } = await axiosInstance.get(`/equivalency/request/${requestId}`);
    return data;
  }

  static renameDocumentsName(documents: Array<AppFile>) {
    const result = documents.map(file => {
      const fileType = file.file_type?.toLowerCase() ?? 'default';
      const fileNameParts = file.file_name.split('.');
      const fileExtension = fileNameParts.pop();
      const newFileName = `${fileType}.${fileExtension}`;
      return { ...file, file_name: newFileName };
    });
    return result;
  }

  static renameActivitiesFileName(activities: Array<RequestActivity>) {
    const result = activities.map((activity, index) => {
      if (activity.program_file) {
        const tempProgramFile = activity.program_file;
        const newName = `activity${index + 1}.pdf`;
        return {
          ...activity,
          program_file: {
            ...tempProgramFile,
            file_name: newName
          }
        }
      }
      return activity;
    });
    return result;
  }

  static async rejectRequest(params: EquivalencyRequestParams, request: EquivalencyRequest) {
    const newToken = params.generateToken && await params.generateToken();
    const rejectedActivities = request.activities.map((activity: RequestActivity) => ({
      ...activity,
      state: EquivalencyRequestActivityStates.rejected
    }))
    // reject request with all its activities rejected
    const updatedRequest: EquivalencyRequest = {
      ...request,
      observation: t('rejectedRequestFailFiles'),
      reCaptchaToken: newToken,
      activities: rejectedActivities
    }
    const { data } = await axiosInstance.put<EquivalencyRequest>(`/equivalency/request/${request.id}`, updatedRequest);
    return data;
  }

  static async uploadActivityFiles(activity: RequestActivity) {

    if (!activity.program_file?.url || !activity.program_file?.file) {
      throw new Error('error trying to upload document file');
    }

    const file: File = activity.program_file.file;
    return await API.uploadFile(file, activity.program_file.url);
  }

  static async uploadDocumentFiles(file: AppFile) {
    if (!file.url || !file.file) {
      throw new Error('error trying to upload document file');
    }

    const documentFile: File = file.file;
    await API.uploadFile(documentFile, file.url);
  }

  static async getEquivalencyRuleActivity(parameters: EquivalencyRuleSearchParams) {
    const { data } = await axiosInstance.get('/equivalency/rule/activity', {
      params: parameters
    });
    return data;
  }

  static async getUserProfile() {
    const { data } = await axiosInstance.get('/users/profile');
    return data;
  }

  static async uploadFile(file: File, url: string) {
    const headers = {
      'Content-Type': file.type
    };

    const { data } = await axios.put(url, file, { headers });
    return data;
  }

  static async postStudentFormHubspot(fieldsData: FormDataHubspotForms[], portalId: string, formGuid: string) {
    const formData = {
      fields: fieldsData
    };
    const urlHubspot = getPostFormHubspotUrl(portalId, formGuid);
    const data = await axios.post(urlHubspot, formData,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      });
    return data;
  }

  static async postDniValidation(imageBytes: string, personalId: string, urlSettings: string) {
    const url = urlSettings;
    const body = {
      image: imageBytes,
      personalId
    };
    const response = await axios.post(url, body, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  }

  static async createDeal(dealData: DealData) {
    const { data } = await axiosInstance.post('/hubspot/deal', dealData,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      });
    return data;
  }

  static async updateDeal(dealData: DealData) {
    const { data } = await axiosInstance.patch('/hubspot/deal', dealData);
    return data;
  }

  static async postHubspotContact(hubspotData: HubspotContactParams) {
    const data = await axiosInstance.post('/hubspot/contact', hubspotData,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      });
    return data;
  }

  static async postReviewRequest(reviewData: ReviewRequesttParams) {
    const data = await axiosInstance.post('/equivalency/request/createReviewRequest', reviewData,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      });
    return data;
  }
}

export default API;
